import api from '@utils/api';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import SwiperCore, { Keyboard, Navigation } from 'swiper';
import { Swiper as SwiperInstance, SwiperSlide } from 'swiper/react';
import { useTranslation } from 'react-i18next';
import useBreakpoint from '@utils/useBreakpoint';
import classNames from 'classnames';
import '@moncler/scss/components/products-carousel-simplified/products-carousel-simplified.scss';
import einsteinutils from '@common/utils/einsteinutils';
import axios from 'axios';
import analytics from '@common/utils/analytics';
import ProductTile from '../listing/ProductTile';
SwiperCore.use([Keyboard, Navigation]);
const cancelTokenSource = axios.CancelToken.source();
const ProductsCarouselSImplified = (props) => {
    var _a;
    const { uuid, pageId } = props;
    const { t } = useTranslation();
    const [component, setComponent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(0);
    const [activeProducts, setActiveProducts] = useState(null);
    const currentBreakpoint = useBreakpoint();
    const swiperRef = useRef(null);
    const prevRef = useRef(null);
    const nextRef = useRef(null);
    const isInBlackFlp = !!props.inBlackFlp;
    const loaderTile = () => (React.createElement("div", { className: "product-tile-carousel initialized" },
        React.createElement("div", { className: "product-tile product-tile--carousel skeleton-tile" },
            React.createElement("div", { className: "product-image" }),
            React.createElement("div", { className: "product-tile__info single-slide__labels" },
                React.createElement("p", { className: "product-tile__name" }, "Loading..."),
                React.createElement("span", { className: "product-tile__price" }, "Loading...")))));
    const renderLoader = () => (React.createElement("div", { className: "skeleton-loading" },
        loaderTile(),
        loaderTile(),
        loaderTile(),
        currentBreakpoint >= window.mqObj.md && loaderTile()));
    const swiperProps = {
        slidesPerView: 'auto',
        spaceBetween: 16,
        breakpoints: {
            600: {
                slidesPerView: 3,
            },
            1024: {
                slidesPerView: 4,
            },
        },
        threshold: 5,
        watchSlidesProgress: true,
        resistance: true,
        resistanceRatio: 0,
    };
    function handleDataResponse(data) {
        setActiveProducts(data);
        setLoading(false);
        // analytics.productImpressionsRecommended(
        //   data,
        //   `themed products_${component?.data?.title?.toLowerCase()}_${component.data.tabs[activeTab]?.title?.toLowerCase()}_${window.datalayerdata.pageCategory}`
        // );
        analytics.productImpressionsRecommended(data, `recommended_${props.pageType}`);
    }
    const showContent = useMemo(() => {
        var _a, _b, _c;
        const showTabs = (_a = component === null || component === void 0 ? void 0 : component.data) === null || _a === void 0 ? void 0 : _a.tabs.some((e) => !!e.title && (!!e.cat || !!e.rec));
        const showLinks = (_c = (_b = component === null || component === void 0 ? void 0 : component.data) === null || _b === void 0 ? void 0 : _b.links) === null || _c === void 0 ? void 0 : _c.some((e) => !!e.title && !!e.url);
        return {
            tabs: showTabs,
            links: showLinks,
        };
    }, [component]);
    useEffect(() => {
        api.getPageJson(pageId, true).then((data) => {
            var _a, _b, _c;
            let component = (_c = (_b = (_a = data === null || data === void 0 ? void 0 : data.regions) === null || _a === void 0 ? void 0 : _a.find((el) => el.id === 'main')) === null || _b === void 0 ? void 0 : _b.components) === null || _c === void 0 ? void 0 : _c.find((el) => el.id === uuid);
            if (component) {
                component = Object.assign(Object.assign({}, component), { data: Object.assign(Object.assign({}, component.data), component.custom) });
                setComponent(component);
            }
        });
    }, []);
    useEffect(() => {
        var _a, _b;
        if ((_a = component === null || component === void 0 ? void 0 : component.data) === null || _a === void 0 ? void 0 : _a.tabs[activeTab]) {
            setLoading(true);
            const currentTab = component.data.tabs[activeTab];
            if (currentTab === null || currentTab === void 0 ? void 0 : currentTab.productsMultiline) {
                const pids = (_b = currentTab.productsMultiline) === null || _b === void 0 ? void 0 : _b.split('\n');
                api.getProductTiles(pids).then((data) => {
                    handleDataResponse(data);
                    setLoading(false);
                });
            }
            else if (currentTab === null || currentTab === void 0 ? void 0 : currentTab.cat) {
                api.getProductTilesFromCategory(currentTab.cat).then((data) => {
                    handleDataResponse(data);
                    setLoading(false);
                });
            }
            else if (currentTab.rec) {
                einsteinutils.productRecommendations(currentTab.rec, (rec) => {
                    var _a;
                    if ((_a = rec === null || rec === void 0 ? void 0 : rec.products) === null || _a === void 0 ? void 0 : _a.length) {
                        handleDataResponse(rec.products);
                    }
                    setLoading(false);
                }, cancelTokenSource);
            }
            else {
                setLoading(false);
            }
            return () => {
                setActiveProducts(null);
                cancelTokenSource.cancel();
            };
        }
    }, [component, activeTab]);
    if (!component)
        return React.createElement(React.Fragment, null);
    const arrowClassName = (direction) => {
        return `icon--chevron-${direction}-big${props.inBlackFlp ? '-white' : ''}`;
    };
    const renderArrows = () => (React.createElement("div", { className: classNames('products-carousel-simplified__arrows', { 'only-title': !showContent.links && !showContent.tabs }) },
        React.createElement("button", { type: "button", ref: prevRef, className: `${props.uuid}--prev`, "aria-label": t('carousel.prevslidemessage') },
            React.createElement("i", { className: arrowClassName('left'), "aria-hidden": true })),
        React.createElement("button", { type: "button", ref: nextRef, className: `${props.uuid}--next`, "aria-label": t('carousel.nextslidemessage') },
            React.createElement("i", { className: arrowClassName('right'), "aria-hidden": true }))));
    const renderTabs = () => (React.createElement("div", { className: "products-carousel-simplified__tabs container" },
        React.createElement("ul", { className: "tabs" }, component.data.tabs.map((el, i) => (el === null || el === void 0 ? void 0 : el.title) && (React.createElement("li", { key: `${el}-${i}`, className: classNames('tab', { 'is-active': activeTab == i }) },
            React.createElement("button", { onClick: () => setActiveTab(i) },
                React.createElement("span", { className: "primary-xs-u" }, el.title))))))));
    const renderLinks = () => (React.createElement("div", { className: "products-carousel-simplified__links container" }, component.data.links.map((el) => el.title &&
        el.url && (React.createElement("a", { key: el.title + el.url, href: el.url, "aria-describedby": props.uuid, "data-analytics": `{"event": "click", "eventCategory": "${props.pageType}", "eventAction": "${props.pagetitleforanalytics || props.title}", "eventLabel": "${el.analytics || el.title}"}`, className: "button button--tertiary button--arrow-right" }, el.title)))));
    const renderCarousel = () => (React.createElement("div", { className: "products-carousel-simplified__carousel container" }, loading ? (renderLoader()) : (React.createElement(React.Fragment, null, !!(activeProducts === null || activeProducts === void 0 ? void 0 : activeProducts.length) && (React.createElement(SwiperInstance, Object.assign({}, swiperProps, { navigation: {
            prevEl: prevRef.current,
            nextEl: nextRef.current,
        }, onBeforeInit: (swiper) => {
            //@ts-ignore disable-next-line
            swiper.params.navigation.prevEl = prevRef.current;
            //@ts-ignore disable-next-line
            swiper.params.navigation.nextEl = nextRef.current;
        }, onInit: (swiper) => (swiperRef.current = swiper) }), activeProducts.map((el, i) => (React.createElement(SwiperSlide, { key: el.uuid },
        React.createElement(ProductTile, { product: el, type: "RECS", index: i, compressColorSwatch: true, hidelabels: true, showonlyname: true, isSimplified: true, forceWhiteBg: isInBlackFlp, hideWishlistBtn: isInBlackFlp, hideCarouselPagination: isInBlackFlp, pageType: props.pageType, 
            //@ts-ignore disable-next-line
            withCarousel: currentBreakpoint >= window.mqObj.md, noSwatch: true }))))))))));
    return (React.createElement("div", { className: classNames('products-carousel-simplified', { genius: !!props.inBlackFlp }) },
        React.createElement("div", { className: "products-carousel-simplified__intro" },
            React.createElement("div", { className: classNames('products-carousel-simplified__title container', { 'next-tabs': showContent.tabs }, { 'next-links': showContent.links }) }, ((_a = component.data) === null || _a === void 0 ? void 0 : _a.title) && React.createElement("h2", { className: "primary-l-u" }, component.data.title)),
            showContent.links && renderLinks(),
            showContent.tabs && renderTabs(),
            currentBreakpoint >= window.mqObj.md && renderArrows()),
        renderCarousel(),
        currentBreakpoint < window.mqObj.md && renderArrows()));
};
export default ProductsCarouselSImplified;
